import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { ShowLink } from '../../components/portal/ShowLink';
import { ShowFields } from '../../components/portal/ShowFields';
import { rowStatusLabel } from './Projects';
import { ProjectAdmin } from './ProjectAdmin';
import { showStatus, showUpdated } from '../../utils/status';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD } from '../../components/portal/adminStyles';

/**
 * The staff-side home for a show with more than one episode — reached by
 * clicking a multi-episode row in Active Projects. Lists every episode and
 * lets staff add a new one directly, without first opening an existing
 * episode. The show's one link (never a per-episode link) lives here too.
 *
 * Clicking an episode expands its full admin panel inline, right below the
 * row, instead of navigating to a separate page — one at a time, so
 * switching between episodes never means leaving Show Admin and re-entering
 * it from Active Projects.
 */
export function ShowAdmin() {
  const {
    activeAdminShow,
    adminShowProjects,
    selectProject,
    goPortal,
    addEpisodeToShow,
    notify
  } = usePortal();
  const [addingEpisode, setAddingEpisode] = useState(false);
  const [episodeName, setEpisodeName] = useState('');
  /** One episode expanded at a time — its admin panel reads `activeProject`
      from context, so only one can meaningfully be open at once anyway. */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingShow, setEditingShow] = useState(false);
  function toggleEpisode(id: string) {
    setExpandedId(current => {
      const next = current === id ? null : id;
      if (next) selectProject(next);
      return next;
    });
  }
  const show = activeAdminShow;
  if (!show) {
    return <div data-id="element-2495" data-mp="o9fhwy7iuxt6">
        <button type="button" onClick={() => goPortal('projects')} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-2496" data-mp="j8hqdv0mgrf0">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-2497" data-mp="2o92a7tdur4g6" />
          Client Projects
        </button>
        <p className="mt-8 font-display text-2xl text-paper/40" data-id="element-2498" data-mp="2xbldgeljvj2y">This show could no longer be found.</p>
      </div>;
  }
  function submitEpisode(event: React.FormEvent) {
    event.preventDefault();
    const name = episodeName.trim();
    if (!name) return;
    addEpisodeToShow(show.id, name);
    setEpisodeName('');
    setAddingEpisode(false);
    notify(`${name} added to ${show.name}`);
  }
  const count = adminShowProjects.length;
  return <div data-id="element-2499" data-mp="3g2uo4r724dw4">
      <button type="button" onClick={() => goPortal('projects')} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-2500" data-mp="3l0cwogh5wr3l">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-2501" data-mp="3y9tx8ikddyj" />
        Client Projects
      </button>

      {/* The show itself is the anchor — same header shape as a single
          project's admin page, one level up. */}
      <header className="mt-8" data-id="element-2502" data-mp="3m4odbmzlqg4b">
        <p className="label text-paper/40" data-id="element-2503" data-mp="3m5pri0bwziq9">{show.client}</p>
        <h1 className="mt-5 font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2504" data-mp="mmwupetpjh6b">
          {show.name}
        </h1>
        {/* Status and last-updated read the same way a single project's own
            admin page does — derived from every episode's deliverables at
            once, so a show never looks less informative than the project
            it's grouping together. */}
        <p className="label mt-6 text-paper/40" data-id="element-2505" data-mp="15fr9a7nf57a0">
          {showStatus(adminShowProjects)} · {count} episode{count === 1 ? '' : 's'} · Last updated{' '}
          {showUpdated(adminShowProjects) || '—'}
        </p>

        {/* The one link that ever goes to this client for this show — every
            episode is reached through it, so it lives here, not per episode.
            Same Copy Link / Preview as Client treatment as a standalone
            project's own Client Link, rather than the compact text-link
            version used from the projects list. */}
        <ShowLink slug={show.slug} showId={show.id} data-id="element-2506" data-mp="13wqz9dlysm37" />

        {/* Client name, type, contact and description live here — for the
            show as a whole — rather than only being reachable by opening a
            specific episode first and finding them among ITS settings. One
            button only: a separate "Show Settings" label above it read as a
            second, non-working control rather than a heading. */}
        <div className="mt-6" data-id="element-2507" data-mp="1muadcvna4uwh">
          <button type="button" onClick={() => setEditingShow(open => !open)} className={BUTTON_QUIET} data-id="element-2508" data-mp="1mu1xqvzrvbhl">
            {editingShow ? 'Close Editor' : 'Edit Show'}
          </button>
        </div>

        {editingShow && <ShowFields show={show} onClose={() => setEditingShow(false)} data-id="element-2509" data-mp="37e5oq77m9rsv" />}
      </header>

      {/* No "Episodes" heading here — the header above already says "2
          episodes", so a second label repeating the same word right below
          it, plus the divider that came with it, was pure repetition. The
          list speaks for itself. */}
      <section aria-label="Episodes" className="mt-12" data-id="element-2510" data-mp="2tfpbryj5v52a">
        <ul className="m-0 list-none p-0" data-id="element-2511" data-mp="2ofl2otkl1cm3">
          {adminShowProjects.map(row => {
          const expanded = expandedId === row.id;
          return <li key={row.id} className="border-b border-ruleDark" data-id="element-2512" data-mp="2ycz7tx122ez3">
                <button type="button" onClick={() => toggleEpisode(row.id)} className="group grid w-full grid-cols-1 items-baseline gap-x-8 gap-y-3 py-8 text-left sm:grid-cols-12" data-id="element-2513" data-mp="2yesgtiz55ba9">
                  <span className="font-display text-3xl leading-none sm:col-span-9 sm:text-4xl" data-id="element-2514" data-mp="kqgb07nln7e8">
                    {row.name}
                  </span>
                  <span className="label flex items-center gap-4 text-paper sm:col-span-3 sm:justify-self-end" data-id="element-2515" data-mp="pqkve8rmom9q">
                    {expanded ? 'Hide' : rowStatusLabel(row)}
                    <ArrowRightIcon className={['h-3.5 w-3.5 shrink-0 transition-transform duration-150 ease-out', expanded ? '-rotate-90 opacity-100' : 'rotate-90 opacity-40 group-hover:translate-x-1 group-hover:opacity-100'].join(' ')} strokeWidth={1.25} data-id="element-2516" data-mp="zmfknmqcxflh" />
                  </span>
                </button>
                {expanded && <div className="border-t border-ruleDark pb-4 pt-8 pl-0 sm:pl-8" data-id="element-2517" data-mp="3fc91ch51o46j">
                    <ProjectAdmin embedded data-id="element-2518" data-mp="3kb271ne3aqw5" />
                  </div>}
              </li>;
        })}
          {count === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-2519" data-mp="1xhi5fyijo0v2">
              Nothing here yet.
            </li>}
        </ul>

        {addingEpisode ? <form onSubmit={submitEpisode} className="mt-8 max-w-md" data-id="element-2520" data-mp="1nj2bzow1ofie">
            <label className="block" data-id="element-2521" data-mp="1ij68rrv68s3w">
              <span className="label block text-paper/40" data-id="element-2522" data-mp="1ik7typ5wiuzc">Episode Name</span>
              <input value={episodeName} onChange={event => setEpisodeName(event.target.value)} placeholder="e.g. Episode 3" autoFocus className={FIELD} data-id="element-2523" data-mp="1drxxeqaa6wo3" />
            </label>
            <div className="mt-6 flex items-center gap-6" data-id="element-2524" data-mp="14pyh6073bwm7">
              <button type="submit" className={BUTTON_SOLID} data-id="element-2525" data-mp="zrdhq2znxgyt">
                Add Episode
              </button>
              <button type="button" onClick={() => setAddingEpisode(false)} className={BUTTON_QUIET} data-id="element-2526" data-mp="uudkmhksjoju">
                Cancel
              </button>
            </div>
          </form> : <button type="button" onClick={() => setAddingEpisode(true)} className={`${BUTTON_QUIET} mt-8`} data-id="element-2527" data-mp="2nourwst5f3zm">
            Add Episode
          </button>}
      </section>
    </div>;
}